/**
 * One mark at a time, from a dialog or a button (PRD-0075 R-C3, R-C4): which dialog is open, the
 * mark it sends, and its outcome — a toast and a closed dialog, or the error in the open dialog
 * (in a toast when the mark had none, an undo).
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { markErrorMessage } from './fact-forms'
import { type FactMark, useFactMark } from './useFactMark'

/** The toast a mark ends with. */
export type MarkOutcome = 'asserted' | 'invalidated' | 'revalidated' | 'retired' | 'restored'

export function useMarkDialogs<D extends string>(caseId: string) {
  const { t } = useTranslation()
  const mark = useFactMark(caseId)
  const [dialog, setDialog] = useState<D | null>(null)
  const send = (fact: FactMark, outcome: MarkOutcome) =>
    mark.mutate(fact, {
      onSuccess: () => {
        setDialog(null)
        toast.success(t(`analystFacts.toasts.${outcome}`))
      },
      onError: error => {
        if (dialog === null) toast.error(markErrorMessage(error, t))
      },
    })
  return {
    dialog,
    open: (next: D) => {
      mark.reset()
      setDialog(next)
    },
    close: () => setDialog(null),
    send,
    pending: mark.isPending,
    error: mark.error && dialog !== null ? markErrorMessage(mark.error, t) : null,
  }
}
